/**
 * useAnalytics \u2014 privacy-safe, self-hosted event tracking.
 *
 * - Anonymous session ID (NOT the entitlement token). Rotates every 30 days.
 * - Fire-and-forget: never blocks the UI, never surfaces errors.
 * - No third-party trackers, no PII, no chat text.
 */
import { useCallback, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_KEY = "wingman_analytics_session";
const SESSION_STARTED_KEY = "wingman_analytics_started_at";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getSessionId() {
  if (typeof window === "undefined") return "anon";
  const started = Number(window.localStorage.getItem(SESSION_STARTED_KEY) || 0);
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id || Date.now() - started > MAX_AGE_MS) {
    id = newId();
    window.localStorage.setItem(SESSION_KEY, id);
    window.localStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
  }
  return id;
}

function isMobile() {
  if (typeof window === "undefined") return null;
  const w = window.innerWidth || 0;
  const ua = (navigator.userAgent || "").toLowerCase();
  return /mobi|android|iphone|ipad|ipod/.test(ua) || w < 640;
}

const client = axios.create({ baseURL: API, timeout: 3000 });

export function trackEvent(name, extra = {}) {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      name,
      session_id: getSessionId(),
      path: window.location.pathname + window.location.search.slice(0, 60),
      viewport_w: window.innerWidth || null,
      is_mobile: isMobile(),
      referrer: document.referrer || null,
      ...extra,
    };
    // Also attach as a header so backend event handlers (analyze, checkout)
    // can associate their server-side events with the same session.
    client.post("/analytics/event", payload).catch(() => {});
  } catch {
    // Never let analytics break UX.
  }
}

/** Returns the analytics session id so callers can send it as a header
 * on same-request server events (analyze, checkout). */
export function getAnalyticsSessionId() {
  return getSessionId();
}

/** Fire a pageview on mount (idempotent per session). */
export function usePageview() {
  useEffect(() => {
    trackEvent("pageview");
  }, []);
}

export function useTrack() {
  return useCallback(trackEvent, []);
}
